import React from 'react';
import { Server, Network, Layers3, Bot, ShieldCheck } from 'lucide-react';

/**
 * EnterpriseAIClip
 * A tall, hero-friendly clip that loops through three stages:
 *   1. Observe — servers, network, applications stream telemetry
 *   2. AI Agents heal — an incident flips Detected → Analyzing → Remediating → Resolved
 *   3. Measurable value — KPI counters tick up
 * Pure SVG + CSS, no external assets.
 */

const Spark = ({ delay = 0 }) => (
  <svg viewBox="0 0 80 20" className="w-16 h-5" preserveAspectRatio="none" aria-hidden>
    <defs>
      <linearGradient id={`spark-g-${delay}`} x1="0" x2="1">
        <stop offset="0%"  stopColor="#60a5fa" />
        <stop offset="100%" stopColor="#22d3ee" />
      </linearGradient>
    </defs>
    <path
      d="M 0 12 L 10 8 L 20 14 L 30 6 L 40 11 L 50 4 L 60 10 L 70 5 L 80 9"
      fill="none" stroke={`url(#spark-g-${delay})`} strokeWidth="1.4"
      strokeDasharray="200" style={{ animation: `eai-spark 3.2s linear infinite`, animationDelay: `${delay}s` }}
    />
  </svg>
);

const ObserveRow = ({ icon: Icon, label, value, status, delay = 0 }) => {
  const dot =
    status === 'warn' ? 'bg-amber-400' :
    status === 'down' ? 'bg-red-400' : 'bg-emerald-400';
  return (
    <div className="flex items-center gap-3 py-2">
      <div className="w-8 h-8 rounded-lg bg-neutral-950 border border-neutral-800 flex items-center justify-center flex-shrink-0">
        <Icon className="w-3.5 h-3.5 text-blue-300" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-[12px] text-neutral-200 font-medium">{label}</span>
          <span className={`w-1.5 h-1.5 rounded-full ${dot} eai-dot`} style={{ animationDelay: `${delay * 0.4}s` }} />
        </div>
        <div className="text-[10.5px] text-neutral-500 tabular-nums">{value}</div>
      </div>
      <Spark delay={delay} />
    </div>
  );
};

const EnterpriseAIClip = () => {
  return (
    <div className="relative w-full" data-testid="enterprise-ai-clip">
      <style>{`
        @keyframes eai-spark   { 0% { stroke-dashoffset: 200; } 100% { stroke-dashoffset: 0; } }
        @keyframes eai-dot     { 0%, 100% { opacity: 0.45; transform: scale(1); } 50% { opacity: 1; transform: scale(1.25); } }
        @keyframes eai-ring    { 0% { transform: scale(0.85); opacity: 0.55; } 90% { transform: scale(1.5); opacity: 0; } 100% { opacity: 0; } }
        @keyframes eai-scan    { 0% { transform: translateY(-100%); opacity: 0; } 15% { opacity: 0.55; } 85% { opacity: 0.55; } 100% { transform: translateY(100%); opacity: 0; } }
        @keyframes eai-stage-1 { 0%, 18% { opacity: 1; } 25%, 100% { opacity: 0; } }
        @keyframes eai-stage-2 { 0%, 24% { opacity: 0; } 28%, 48% { opacity: 1; } 55%, 100% { opacity: 0; } }
        @keyframes eai-stage-3 { 0%, 54% { opacity: 0; } 58%, 78% { opacity: 1; } 85%, 100% { opacity: 0; } }
        @keyframes eai-stage-4 { 0%, 84% { opacity: 0; } 88%, 100% { opacity: 1; } }
        @keyframes eai-step    { 0%, 100% { background-color: rgba(255,255,255,0.10); } 50% { background-color: #3b82f6; } }
        @keyframes eai-bar     { 0% { width: 8%; } 100% { width: 92%; } }
        @keyframes eai-count-1 { 0% { content: "8,420"; } 100% { content: "12,480"; } }
        @keyframes eai-count-2 { 0% { content: "2m 14s"; } 50% { content: "1m 09s"; } 100% { content: "47s"; } }
        @keyframes eai-count-3 { 0% { content: "78%"; } 100% { content: "92%"; } }
        .eai-dot     { animation: eai-dot 1.8s ease-in-out infinite; transform-origin: center; }
        .eai-scan    { animation: eai-scan 6s ease-in-out infinite; }
        .eai-stage-1 { animation: eai-stage-1 8s linear infinite; }
        .eai-stage-2 { animation: eai-stage-2 8s linear infinite; }
        .eai-stage-3 { animation: eai-stage-3 8s linear infinite; }
        .eai-stage-4 { animation: eai-stage-4 8s linear infinite; }
        .eai-step    { animation: eai-step 2s ease-in-out infinite; }
        .eai-bar     { animation: eai-bar 8s linear infinite; }
        .eai-ring    { animation: eai-ring 2.4s ease-out infinite; transform-origin: center; }
        .eai-count-1::after { content: "12,480"; animation: eai-count-1 6s linear infinite; }
        .eai-count-2::after { content: "47s";    animation: eai-count-2 6s linear infinite; }
        .eai-count-3::after { content: "92%";    animation: eai-count-3 6s linear infinite; }
      `}</style>

      <div className="relative rounded-3xl border border-neutral-800 bg-gradient-to-br from-neutral-950 via-black to-neutral-950 overflow-hidden">
        {/* Background grid + scan */}
        <div aria-hidden className="absolute inset-0 opacity-[0.18] pointer-events-none" style={{
          backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.06) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
          maskImage: 'radial-gradient(ellipse at center, black 50%, transparent 90%)',
        }} />
        <div aria-hidden className="absolute inset-x-0 top-0 h-full pointer-events-none">
          <div className="eai-scan absolute inset-x-0 h-28" style={{
            background: 'linear-gradient(to bottom, transparent, rgba(59,130,246,0.16), transparent)',
            filter: 'blur(8px)',
          }} />
        </div>

        {/* Header */}
        <div className="relative px-5 pt-5 pb-3 flex items-center justify-between">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 backdrop-blur border border-white/15 text-[11px] font-medium text-white">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 eai-dot" />
            Live demo
          </div>
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-neutral-500">Observe · Heal · Value</div>
        </div>

        {/* Panel 1 — OBSERVE */}
        <div className="relative mx-5 mt-2 rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-[10.5px] uppercase tracking-[0.16em] text-blue-300 font-semibold">Observe</span>
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 eai-dot" />
            </div>
            <span className="text-[10px] text-neutral-500 tabular-nums">stream · 1.0s</span>
          </div>
          <div className="mt-1 divide-y divide-neutral-800/70">
            <ObserveRow icon={Server}   label="Servers"      value="1,284 healthy"     status="ok"   delay={0}   />
            <ObserveRow icon={Network}  label="Network"      value="42 paths · 0 loss" status="warn" delay={0.6} />
            <ObserveRow icon={Layers3}  label="Applications" value="318 services"      status="ok"   delay={1.2} />
          </div>
        </div>

        {/* Panel 2 — AI AGENT HEALING */}
        <div className="relative mx-5 mt-3 rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-[10.5px] uppercase tracking-[0.16em] text-blue-300 font-semibold">AI Agent · Self-Heal</span>
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 eai-dot" />
            </div>
            <span className="text-[10px] text-neutral-500 tabular-nums">incident · INC-204</span>
          </div>

          <div className="mt-3 flex items-center gap-3">
            {/* Agent avatar with pulse */}
            <div className="relative flex-shrink-0">
              <div className="absolute inset-0 rounded-full border border-blue-500 eai-ring" />
              <div className="absolute inset-0 rounded-full border border-blue-500 eai-ring" style={{ animationDelay: '1.2s' }} />
              <div className="relative w-10 h-10 rounded-full bg-gradient-to-br from-blue-700 to-blue-950 border border-blue-500/60 flex items-center justify-center">
                <Bot className="w-4 h-4 text-blue-100" />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="relative h-[18px]">
                <span className="absolute inset-0 inline-flex items-center gap-1.5 text-[12.5px] text-amber-300 font-semibold eai-stage-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 eai-dot" /> Detected anomaly · API latency
                </span>
                <span className="absolute inset-0 inline-flex items-center gap-1.5 text-[12.5px] text-blue-300 font-semibold eai-stage-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400 eai-dot" /> Analyzing root cause…
                </span>
                <span className="absolute inset-0 inline-flex items-center gap-1.5 text-[12.5px] text-blue-200 font-semibold eai-stage-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-300 eai-dot" /> Remediating · scaling pod-7
                </span>
                <span className="absolute inset-0 inline-flex items-center gap-1.5 text-[12.5px] text-emerald-300 font-semibold eai-stage-4">
                  <ShieldCheck className="w-3.5 h-3.5" /> Resolved · service stable
                </span>
              </div>
              <div className="mt-2 h-1 rounded-full bg-neutral-900 overflow-hidden">
                <div className="eai-bar h-full" style={{ background: 'linear-gradient(to right, #3b82f6, #22c55e)' }} />
              </div>
              <div className="mt-2 flex items-center gap-1.5">
                {[0, 1, 2, 3].map((i) => (
                  <span key={i} className="eai-step h-1 w-6 rounded-full" style={{ animationDelay: `${i * 0.5}s` }} />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Panel 3 — MEASURABLE VALUE */}
        <div className="relative mx-5 mt-3 mb-5 rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-[10.5px] uppercase tracking-[0.16em] text-blue-300 font-semibold">Measurable value</span>
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 eai-dot" />
            </div>
            <span className="text-[10px] text-neutral-500">last 24h</span>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2">
            {[
              { k: 'Hours saved', cls: 'eai-count-1', d: '▲ 32% vs LW' },
              { k: 'MTTR',        cls: 'eai-count-2', d: '▼ from 4.5h'  },
              { k: 'Automation',  cls: 'eai-count-3', d: '▲ +14 pts'    },
            ].map((m) => (
              <div key={m.k} className="rounded-lg border border-neutral-800 bg-neutral-950 px-2.5 py-2">
                <div className="text-[9.5px] uppercase tracking-[0.12em] text-neutral-500">{m.k}</div>
                <div className="text-[15px] font-semibold text-white tabular-nums leading-tight">
                  <span className={m.cls} />
                </div>
                <div className="text-[9.5px] text-emerald-400">{m.d}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnterpriseAIClip;
